import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';

import { EMAIL_REGEX, PropertyTextInput, ReadFile, useHiddenFileSelect } from '@paragon/client-tools';
import { SharedComponentSet } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUser } from '@paragon/login-tools';
import { enqueueError, enqueueSuccess } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { DialogTitleWithBackButton } from './DialogTitleWithBackButton';

interface Props {
  sharedComponentSet: SharedComponentSet;
  titleMode: 'seal' | 'repair' | 'normal';
  onNavigationBackRequested: () => void;
}

export function SendLinkForm({ sharedComponentSet, titleMode, onNavigationBackRequested }: Props) {
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(getLoggedInUser);

  const [isWorking, setIsWorking] = useState(false);

  const [fromEmail, _setFromEmail] = useState<string>(loggedInUser?.email ?? '');
  const [fromEmailError, _setFromEmailError] = useState<boolean>(false);
  function setFromEmail(newFromEmail: string) {
    _setFromEmail(newFromEmail.trim());
    _setFromEmailError(newFromEmail.trim() === '' || !EMAIL_REGEX.test(newFromEmail));
  }

  const [toEmailAddressesString, setToEmailAddressesString] = useState<string | null>(null);
  const toEmailAddresses = toEmailAddressesString?.split(',').map((emailAddress) => emailAddress.trim());
  const isShareWithEmailError = toEmailAddresses
    ? toEmailAddresses.some((emailAddress) => !EMAIL_REGEX.test(emailAddress))
    : false;

  const [subject, _setSubject] = useState(
    titleMode === 'seal'
      ? `Seals Requested: ${sharedComponentSet.name}`
      : titleMode === 'repair'
        ? `Repair Requested: ${sharedComponentSet.name}`
        : `Shared Trusses: ${sharedComponentSet.name}`,
  );
  const [subjectError, _setSubjectError] = useState(false);
  function setSubject(newSubject: string) {
    _setSubject(newSubject.trim());
    _setSubjectError(newSubject.trim() === '');
  }

  const [message, _setMessage] = useState<string | null>(
    titleMode === 'normal'
      ? null
      : 'Please review these trusses and reply with sealed TDDs or a description of what needs to change.',
  );
  function setMessage(newMessage: string) {
    _setMessage(newMessage.trim() === '' ? null : newMessage.trim());
  }

  const [renderHiddenSelect, selectFile] = useHiddenFileSelect('*', true);
  const [selectedFiles, setSelectedFiles] = React.useState<readonly ReadFile[]>([]);
  async function onSelectFilesToInclude() {
    const chosenFiles = await selectFile();
    setSelectedFiles((oldState) => [...oldState, ...chosenFiles]);
  }

  return (
    <Dialog
      insistent
      variant="design"
      hasTransition={false}
      title={<DialogTitleWithBackButton title="Email Truss Design Drawings" onBackClick={onNavigationBackRequested} />}
      onCancel="close"
      submitText={isWorking ? 'Sending Link...' : 'Send Link'}
      submitDisabled={
        isWorking ||
        !toEmailAddressesString ||
        !toEmailAddresses ||
        isShareWithEmailError ||
        !fromEmail ||
        fromEmailError ||
        subjectError
      }
      onSubmit={async () => {
        setIsWorking(true);

        let success = false;

        try {
          await designService.sharedComponentSets.share(
            sharedComponentSet!.lookupCode,
            fromEmail,
            toEmailAddresses!,
            subject,
            message,
            selectedFiles,
          );
          success = true;
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(closeCurrentPopup());
        }

        if (success) {
          dispatch(enqueueSuccess('Emails sent!'));
        } else {
          dispatch(enqueueError('Error sending emails to share components. Contact help@paragontruss.com.'));
        }
      }}
    >
      {renderHiddenSelect()}

      <Stack spacing={1} sx={{ marginTop: 2 }}>
        <PropertyTextInput
          fullWidth
          property="Contact email"
          value={fromEmail}
          disabled={isWorking}
          submit={setFromEmail}
          error={fromEmailError}
        />

        <PropertyTextInput
          fullWidth
          property="To email(s) separated by commas"
          value={toEmailAddressesString ?? ''}
          disabled={isWorking}
          submit={(newValue) => setToEmailAddressesString(newValue === '' ? null : newValue)}
          error={isShareWithEmailError}
        />

        <PropertyTextInput
          fullWidth
          property="Subject"
          value={subject}
          disabled={isWorking}
          submit={setSubject}
          error={subjectError}
        />

        <PropertyTextInput
          fullWidth
          multiline
          property="Message"
          value={message ?? ''}
          disabled={isWorking}
          submit={setMessage}
        />
      </Stack>

      <Button variant="outlined" disabled={isWorking} onClick={onSelectFilesToInclude}>
        {selectedFiles.length > 0 ? 'Add More Files' : 'Select Extra Files to Include (available for 90 days)'}
      </Button>

      {selectedFiles.length > 0 && <Typography>Selected files:</Typography>}
      {selectedFiles.map((file) => (
        <Typography key={`${file.filename}.${file.filetype}`}>{`${file.filename}.${file.filetype}`}</Typography>
      ))}
      {selectedFiles.length > 0 && (
        <Button variant="outlined" disabled={isWorking} onClick={() => void setSelectedFiles([])}>
          Clear Selected Files
        </Button>
      )}
    </Dialog>
  );
}
